import React, { useEffect, useMemo, useState } from "react";
import { Users, Building2, Landmark, Ruler, MapPin, Globe } from "lucide-react";
import { getPlaceFacts } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { InlineListen } from "@/components/site/ListenControl";
import { SourceBadge } from "@/components/site/SourceBadge";

const FACT_ICON = {
  population: Users, county: Building2, state: Landmark,
  area: Ruler, coordinates: MapPin, website: Globe,
};

/**
 * Source-governed Quick Facts strip. Every fact carries a reference year,
 * an authoritative source and a verification level (see SourceBadge).
 * Facts come from /api/place-facts (Census PEP/ACS + Census geography).
 */
export function QuickFacts({ geoId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    getPlaceFacts(geoId).then((d) => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [geoId]);

  const readable = useMemo(() => {
    if (!data?.facts?.length) return [];
    return data.facts.map((f) => `${f.label}: ${f.value}${f.year ? `, as of ${f.year}` : ""}.`);
  }, [data]);

  if (!data || !data.facts?.length) return null;
  const { scope, facts } = data;

  return (
    <section className="mx-auto max-w-[1500px] px-4 py-6" data-testid="quick-facts">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-oldglory-red">
            <T>Quick Facts</T>
          </div>
          {scope?.note && (
            <p className="mt-1 max-w-2xl text-[11px] text-muted-foreground" data-testid="quick-facts-scope">
              <T>{scope.note}</T>
            </p>
          )}
        </div>
        <InlineListen paragraphs={readable} label="Listen to facts" testid="quick-facts-listen" />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="quick-facts-grid">
        {facts.map((f) => {
          const Icon = FACT_ICON[f.key] || MapPin;
          return (
            <div
              key={f.key}
              className="flex flex-col rounded-xl border border-border bg-white p-3 transition-colors hover:border-navy/30"
              data-testid={`quick-fact-${f.key}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3.5 w-3.5 text-oldglory-red/70" /> <T>{f.label}</T>
              </div>
              <div className="mt-1 font-display text-lg font-bold leading-tight text-navy">
                {f.value}
              </div>
              <div className="mt-auto pt-2">
                <SourceBadge source={f.source} verification={f.verification} year={f.year} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default QuickFacts;
